import React, { forwardRef } from 'react';
import { CheckIcon } from '@fiverr-private/visuals';
import { assignInlineVars, Box, theme } from '@fiverr-private/theme';
import { Container } from '@fiverr-private/layout_components';
import Input from '../Input';
import type { InputProps } from '../Input/types';
import { filterProps } from '../shared/utils';
import { formsTokenColors } from '../shared/formsTokenColors';
import type { SwitchProps } from './types';
import { input, toggler, swinger, swingerLeftVar, outlineColorVar } from './styles.ve.css';
import { getStylingProps, getValidationColor, SWINGER_MARGIN } from './style';

const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ size = 'md', disabled = false, children, isError = false, isSuccess = false, className, ...rest }, ref) => {
    const { stylingProps, restProps } = filterProps(rest);

    const inputProps: InputProps = {
      type: 'checkbox',
      disabled,
      role: 'switch',
      className: input,
      opacity: '0',
      width: 0,
      height: 0,
      overflow: 'hidden',
      ...restProps,
    };

    const { color, outlineColor } = getValidationColor({ isError, isSuccess, disabled });
    const {
      switcherStylingProps,
      togglerStylingProps,
      swingerStylingProps: { left, ...restSwingerStylingProps },
    } = getStylingProps(size);

    return (
      <Box
        as="label"
        display="inlineFlex"
        color={color}
        className={className}
        {...switcherStylingProps}
        {...stylingProps}
      >
        <Input {...inputProps} ref={ref} />
        <Container
          display="inlineFlex"
          direction="column"
          position="relative"
          borderRadius="circle"
          outlineColor={outlineColor}
          outlineWidth="sm"
          outlineStyle="solid"
          outlineOffset="sm"
          cursor={disabled ? undefined : 'pointer'}
          backgroundColor={disabled ? formsTokenColors.disabledToggleBackgroundColor : 'grey_700'}
          marginRight={children ? '3' : '0'}
          transition={`${theme.animationDuration.fast2} ${theme.animationTiming.easeInOut}`}
          {...togglerStylingProps}
          className={toggler}
        >
          <Container
            display="inlineFlex"
            justifyContent="center"
            alignItems="center"
            position="absolute"
            left={0}
            margin={SWINGER_MARGIN}
            backgroundColor="white"
            borderRadius="circle"
            width="28px"
            height="28px"
            transition={`${theme.animationDuration.fast2} ${theme.animationTiming.easeInOut}`}
            {...restSwingerStylingProps}
            style={assignInlineVars({
              [swingerLeftVar]: `${left}px`,
              [outlineColorVar]: theme.colors[outlineColor],
            })}
            className={swinger}
          >
            <CheckIcon color={disabled ? 'grey_400' : 'grey_1200'} size={size} />
          </Container>
        </Container>
        {children && <Container userSelect="text">{children}</Container>}
      </Box>
    );
  }
);

Switch.displayName = 'Switch';

export default Switch;
